import { useCallback, useEffect, useState } from "react";

import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/ko";

import {
  createVehicleReservation,
  fetchVehicleReservations,
  fetchVehicles,
  updateVehicleReservationStatus,
} from "@/apis/reservation";
import Button from "@/components/Button";
import AlertModal from "@/components/Modal/AlertModal";
import ReservationModal from "@/components/Modal/ReservationModal";
import useModal from "@/hooks/useModal";
import useViewMode from "@/hooks/useViewMode";
import VehicleCalendar from "@/pages/Reservation/components/VehicleReservation/VehicleCalendar";
import VehicleReservationDetail from "@/pages/Reservation/components/VehicleReservation/VehicleReservationDetail";
import VehicleReservationForm, {
  type VehicleFormValues,
} from "@/pages/Reservation/components/VehicleReservation/VehicleReservationForm";
import useLoginStore from "@/store/loginStore";
import useUserStore from "@/store/userStore";
import type {
  Vehicle,
  VehicleReservation as VehicleReservationType,
} from "@/types/vehicleReservation";
import { cn } from "@/utils/cn";
import { hasAllDayReservation } from "@/utils/vehicleAvailability";

dayjs.locale("ko");

const LEADER_ROLES = ["LEADER", "MINISTRY", "ADMIN"];

const ExistingReservations = ({
  reservations,
}: {
  reservations: VehicleReservationType[];
}) => (
  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
    <p className="text-small font-medium text-blue-800">
      이 차량의 예약된 시간
    </p>
    <div className="mt-2 flex flex-col gap-1.5">
      {reservations.map(r => (
        <div
          key={r.id}
          className="flex flex-wrap items-center gap-2 text-small"
        >
          <span
            className={`rounded px-1.5 py-0.5 text-xs font-medium ${
              r.status === "approved"
                ? "bg-green-100 text-green-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            {r.status === "approved" ? "승인" : "대기"}
          </span>
          <span className="font-medium text-blue-900">{r.drivingTime}</span>
          <span className="text-blue-600">- {r.purpose}</span>
          <span className="text-blue-400">({r.applicantName})</span>
        </div>
      ))}
    </div>
  </div>
);

const VehicleReservation = () => {
  const { roleId, userName, telNo, cmtNm } = useUserStore();
  const { tokenId } = useLoginStore();
  const { isAdmin } = useViewMode();
  const isLeaderOrAbove = LEADER_ROLES.includes(roleId);

  const [reservations, setReservations] = useState<VehicleReservationType[]>(
    [],
  );
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(
    null,
  );
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());

  const successModal = useModal();
  const cancelConfirmModal = useModal();
  const alertModal = useModal();
  const [alertMessage, setAlertMessage] = useState("");

  // 차량 목록 로드 (첫 번째 차량 기본 선택)
  useEffect(() => {
    fetchVehicles().then(list => {
      setVehicles(list);
      if (list.length > 0) {
        setSelectedVehicleId(prev => prev ?? list[0].vhclId);
      }
    });
  }, []);

  // 월별 예약 데이터 로드 (전체 차량)
  const loadReservations = useCallback(async () => {
    const thisMonth = currentMonth.format("YYYYMM");
    const nextMonth = currentMonth.add(1, "month").format("YYYYMM");

    const [thisMonthData, nextMonthData] = await Promise.all([
      fetchVehicleReservations(thisMonth),
      fetchVehicleReservations(nextMonth),
    ]);

    const hiddenStatuses = ["rejected", "cancelled", "deleted"];
    const allData = [...thisMonthData, ...nextMonthData];
    setReservations(allData.filter(r => !hiddenStatuses.includes(r.status)));
  }, [currentMonth]);

  useEffect(() => {
    loadReservations();
  }, [loadReservations]);

  // 선택한 차량의 예약만 추림
  const vehicleReservations = reservations.filter(
    r => r.vhclId === selectedVehicleId,
  );

  // 선택 차량 + 선택 날짜의 활성 예약 (승인/대기)
  const dateReservations = selectedDate
    ? vehicleReservations.filter(
        r =>
          r.reservationDate === selectedDate.format("YYYY-MM-DD") &&
          (r.status === "approved" || r.status === "pending"),
      )
    : [];

  // 본인 예약 (취소 가능 여부 판단용)
  const myReservation = dateReservations.find(
    r => r.applicantName === userName,
  );

  const handleSelectDate = (date: Dayjs) => {
    setSelectedDate(date);
  };

  const handleSelectVehicle = (vhclId: number) => {
    setSelectedVehicleId(vhclId);
    setSelectedDate(null);
  };

  const [actionLoading, setActionLoading] = useState(false);

  const handleSubmitReservation = async (data: VehicleFormValues) => {
    if (!selectedDate || selectedVehicleId === null || actionLoading) return;
    setActionLoading(true);

    try {
      await createVehicleReservation({
        vhclId: selectedVehicleId,
        mbrId: tokenId,
        applicantName: userName,
        applicantPhone: telNo || "010-0000-0000",
        communityName: cmtNm || "-",
        rsvtDt: selectedDate.format("YYYYMMDD"),
        drivingTime: data.drivingTime,
        purpose: data.purpose,
        destination: data.destination,
        driverName: data.driverName,
        driverPhone: data.driverPhone,
      });

      await loadReservations();
      successModal.onOpen();
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "예약 신청에 실패했습니다. 다시 시도해주세요.",
      );
      alertModal.onOpen();
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelReservation = async () => {
    if (!myReservation || actionLoading) return;
    setActionLoading(true);

    try {
      await updateVehicleReservationStatus(myReservation.id, "cancelled");
      await loadReservations();
      cancelConfirmModal.onClose();
      setAlertMessage("예약이 취소되었습니다.");
      alertModal.onOpen();
      setSelectedDate(null);
    } catch {
      setAlertMessage("취소에 실패했습니다. 다시 시도해주세요.");
      alertModal.onOpen();
    } finally {
      setActionLoading(false);
    }
  };

  // Render right panel content
  const renderRightPanel = () => {
    // No date selected
    if (!selectedDate) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-gray-dull">
          <p className="text-lg font-medium">날짜를 선택해주세요.</p>
          <div className="text-small">
            <p>예약된 날짜: 상세정보 확인</p>
            <p>빈 날짜: 예약 신청 가능</p>
          </div>
        </div>
      );
    }

    // 과거 날짜: 예약 내역만 표시
    const isPastDate = selectedDate.isBefore(dayjs(), "day");
    if (isPastDate) {
      return (
        <div className="flex flex-col gap-3">
          <div className="text-lg font-semibold text-primary">
            {selectedDate.format("YYYY년 M월 D일 (dd)")}
          </div>
          {dateReservations.length > 0 ? (
            <ExistingReservations reservations={dateReservations} />
          ) : (
            <p className="text-small text-gray-400">예약 내역이 없습니다.</p>
          )}
          <p className="text-xs text-gray-400">
            지난 날짜는 예약할 수 없습니다.
          </p>
        </div>
      );
    }

    // 권한 체크
    if (!isLeaderOrAbove) {
      return (
        <div className="flex flex-col gap-3">
          <div className="text-lg font-semibold text-primary">
            {selectedDate.format("YYYY년 M월 D일 (dd)")}
          </div>
          {dateReservations.length > 0 && (
            <ExistingReservations reservations={dateReservations} />
          )}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-small">
            <p className="font-medium text-amber-800">
              차량 예약은 리더(순장) 이상만 신청 가능합니다.
            </p>
            <p className="mt-1 text-amber-600">
              예약이 필요하시면 소속 공동체 리더에게 문의해주세요.
            </p>
          </div>
        </div>
      );
    }

    // 본인 예약이 있으면 상세 + 취소 가능
    if (myReservation) {
      return (
        <div className="flex flex-col gap-4">
          {dateReservations.length > 1 && (
            <ExistingReservations
              reservations={dateReservations.filter(r => r !== myReservation)}
            />
          )}
          <VehicleReservationDetail
            date={selectedDate}
            reservation={myReservation}
            isOwner
            onCancel={cancelConfirmModal.onOpen}
          />
        </div>
      );
    }

    // 이 차량에 전일 예약이 있으면 추가 예약 불가
    if (hasAllDayReservation(dateReservations)) {
      return (
        <div className="flex flex-col gap-4">
          <div className="text-lg font-semibold text-primary">
            {selectedDate.format("YYYY년 M월 D일 (dd)")}
          </div>
          <ExistingReservations reservations={dateReservations} />
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-small">
            <p className="font-medium text-red-700">
              이 차량은 전일 예약이 있어 추가 예약이 불가합니다.
            </p>
          </div>
        </div>
      );
    }

    // 예약 폼 (기존 예약 표시 포함)
    return (
      <div className="flex flex-col gap-4">
        {dateReservations.length > 0 && (
          <ExistingReservations reservations={dateReservations} />
        )}
        <VehicleReservationForm
          key={`${selectedDate.format("YYYYMMDD")}-${selectedVehicleId}`}
          date={selectedDate}
          vehicle={vehicles.find(v => v.vhclId === selectedVehicleId)}
          existingReservations={dateReservations}
          onSubmit={handleSubmitReservation}
        />
      </div>
    );
  };

  return (
    <>
      <div className="flex w-full flex-col gap-6 md:flex-row md:gap-8">
        {/* Left: Vehicle selector + Calendar */}
        <div className="w-full md:w-[340px] md:min-w-[340px]">
          {/* Vehicle selector */}
          <div className="mb-3 flex gap-2">
            {vehicles.map(v => (
              <button
                key={v.vhclId}
                type="button"
                onClick={() => handleSelectVehicle(v.vhclId)}
                className={cn(
                  "flex-1 rounded-sm border px-3 py-2.5 text-small font-medium transition-colors",
                  v.vhclId === selectedVehicleId
                    ? "border-primary bg-primary text-white"
                    : "border-gray-light bg-white text-gray-dark hover:bg-gray-light/40",
                )}
              >
                {v.vehicleName}
              </button>
            ))}
          </div>

          <div className="rounded-sm border border-gray-light p-4">
            <p className="pb-3 text-center text-base font-medium">날짜 선택</p>
            <VehicleCalendar
              reservations={vehicleReservations}
              selectedDate={selectedDate}
              onSelectDate={handleSelectDate}
              onMonthChange={month => setCurrentMonth(month)}
              unlimited={isAdmin}
            />
          </div>
        </div>

        {/* Right: Info panel */}
        <div className="min-h-[400px] w-full rounded-sm border border-gray-light p-5">
          <p className="pb-4 text-base font-medium text-primary">예약 정보</p>
          {renderRightPanel()}
        </div>
      </div>

      {/* Success modal */}
      {successModal.isOpen && (
        <ReservationModal onClose={successModal.onClose}>
          <ReservationModal.Header>
            차량 예약이 신청되었습니다.
          </ReservationModal.Header>
          <ReservationModal.Content>
            <div className="flex flex-col gap-2 p-4 text-small">
              <p>관리자 승인 후 예약이 확정됩니다.</p>
              <p className="text-gray-dull">
                승인 현황은 예약 페이지에서 확인하실 수 있습니다.
              </p>
            </div>
          </ReservationModal.Content>
          <ReservationModal.Footer>
            <Button
              variant="primary"
              onClick={() => {
                successModal.onClose();
                setSelectedDate(null);
              }}
            >
              확인
            </Button>
          </ReservationModal.Footer>
        </ReservationModal>
      )}

      {/* Cancel confirm modal */}
      {cancelConfirmModal.isOpen && (
        <ReservationModal onClose={cancelConfirmModal.onClose}>
          <ReservationModal.Header>예약 취소</ReservationModal.Header>
          <ReservationModal.Content>
            <div className="flex flex-col gap-2 p-4 text-small">
              <p>정말로 이 예약을 취소하시겠습니까?</p>
              <p className="text-gray-dull">
                취소된 예약은 복구할 수 없습니다.
              </p>
            </div>
          </ReservationModal.Content>
          <ReservationModal.Footer>
            <Button variant="outlined" onClick={cancelConfirmModal.onClose}>
              돌아가기
            </Button>
            <Button variant="primary" onClick={handleCancelReservation}>
              취소하기
            </Button>
          </ReservationModal.Footer>
        </ReservationModal>
      )}

      {/* Alert modal */}
      {alertModal.isOpen && (
        <AlertModal onClose={alertModal.onClose}>{alertMessage}</AlertModal>
      )}
    </>
  );
};

export default VehicleReservation;
